/* eslint-disable ember/no-controller-access-in-routes, no-unused-vars, warp-drive/no-legacy-request-patterns */
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import StudentReportState from '@linc-technologies/linced-common/enums/reports/student-report-state';
import { periodLabelAt, templateDisplayName } from 'linced-app/utils/reporting';
import ReportTemplateSectionType from '@linc-technologies/linced-common/enums/reports/report-template-section-type';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import { loadTemplateWithSections } from 'linced-app/utils/reporting';
import { sortBy } from '@linc-technologies/linced-common/utils/arrays';

export default class ReviewRoute extends Route {
  templateName = 'reporting/review';

  // Services
  @service
  session;

  @service
  store;

  @service
  router;

  @service
  dates;

  // periodIndex identifies which reporting period is being reviewed. Historic
  // cards can route here for a past period, so it cannot be assumed to be the
  // current one. Omitted (e.g. an old bookmark) falls back to the current
  // period, which is the pre-existing behaviour.
  queryParams = {
    periodIndex: { refreshModel: true },
    // Addresses the period across template versions — see the group
    // controller's qpPeriodEnd. Preferred over periodIndex when present.
    periodEndDate: { refreshModel: true },
    // The academic year the period belongs to — see the group controller's
    // qpPeriodYear. Absent means the current year.
    periodYear: { refreshModel: true },
  };

  // Resolves the period when the query param is absent — an old bookmark, or a
  // direct URL. The backend owns which period a template is currently in, so
  // this asks the counts response rather than recomputing it here.
  async _currentPeriodIndex(template) {
    let groupCtrl = this.controllerFor('reporting.group');
    try {
      await groupCtrl.loadGroupReports();
    } catch {
      // Counts unavailable — fall through to the first period rather than
      // failing the transition.
    }
    let entry = (groupCtrl.templateDraftInfo || {})[template.id];
    return entry && typeof entry.periodIndex === 'number'
      ? entry.periodIndex
      : 0;
  }

  async model(params) {
    let template;
    try {
      template = await loadTemplateWithSections(this.store, params.template_id);
    } catch (err) {
      this.router.transitionTo('reporting.group');
      return;
    }

    let group = this.modelFor('reporting.group');

    // includeSections sideloads every report's sections in this one call (the
    // backend batch-loads them), so we don't fan out a separate section query
    // per report — fewer requests and no N+1.
    // Review covers one reporting period at a time. Without narrowing, a
    // template with several periods returns every ready-to-review report
    // across all of them, and the per-student reportMap below silently keeps
    // whichever arrived last.
    // Query params arrive as strings; an absent one means "current period".
    let periodIndex =
      params.periodIndex === undefined ||
      params.periodIndex === null ||
      params.periodIndex === ''
        ? await this._currentPeriodIndex(template)
        : Number(params.periodIndex);

    // filterByPeriodIndex is required: periodIndex alone is ignored by the
    // datastore, since a zero value is indistinguishable from "unset".
    // periodEndDate reaches one period across every version of the template;
    // an index cannot, because a version that inserts or removes a period
    // renumbers the rest. Falls back to the index (against this version alone)
    // only when no date is available — an orphaned index whose period the
    // template no longer declares.
    let periodScope = {};
    if (params.periodEndDate) {
      periodScope.periodEndDate = params.periodEndDate;
    } else {
      periodScope.periodIndex = periodIndex;
      periodScope.filterByPeriodIndex = true;
      periodScope.consolidateTemplateVersions = !template.get('next');
    }
    // The card's own year, not a clock read. A historic card can sit in a past
    // academic year, and filtering that query to the current one asks for the
    // right period in the wrong year and returns nothing. It also names the
    // screen, so it is carried on the model.
    let year =
      params.periodYear === undefined ||
      params.periodYear === null ||
      params.periodYear === ''
        ? this.dates.currentAcademicYear
        : Number(params.periodYear);

    let reviewReports = await this.store.query('reports/student-report', {
      reportTemplateId: template.get('id'),
      schoolId: this.session.activeSchool,
      groupId: group ? group.get('id') : null,
      state: StudentReportState.READY_TO_REVIEW,
      year,
      ...periodScope,
      includeSections: true,
    });

    // Inspect content sections. Every subject referenced by a content section
    // needs its goals/subject record loaded — the strand slider's marker dots
    // read the grade scale from subject.possibleGrades, so without it the slider
    // draws rows but no dots. Progress-visual sections additionally need
    // progressions/levels/expectations for the chart line.
    let contentSections = this.store
      .peekAll('reports/report-template-section')
      .filter(
        (section) =>
          section.belongsTo('reportTemplateId').id() === template.get('id') &&
          section.sectionType === ReportTemplateSectionType.CONTENT,
      );

    let subjectIds = new Set();
    let progressVisualSubjectIds = new Set();
    contentSections.forEach((section) => {
      let config = section.get('configuration');
      if (!config) return;
      try {
        let parsed = typeof config === 'string' ? JSON.parse(config) : config;
        if (!parsed.subjectId) return;
        subjectIds.add(parsed.subjectId);
        let hasProgressVisual = (parsed.components || []).some(
          (c) => c.type === 'progress-visual',
        );
        if (hasProgressVisual) {
          progressVisualSubjectIds.add(parsed.subjectId);
        }
      } catch (err) {
        // ignore
      }
    });

    // Subjects for every content section (drives the strand slider dots).
    // Levels/expectations are needed for every subject regardless of component
    // type — resolveLevelId uses them to save judgement/effort progressions
    // for strand-visual and progress-descriptor sliders too, not just
    // progress-visual charts. Progress-visual subjects additionally need the
    // progression records themselves for the chart line. Fire everything
    // alongside the subject load so every query races in a single parallel
    // pass rather than waiting on subjects first.
    await Promise.all(
      Array.from(subjectIds).map((subjectId) =>
        Promise.all([
          this.store.findRecord('goals/subject', subjectId).catch(() => null),
          this.store
            .query('milestones/level', { goalSubjectId: subjectId })
            .catch(() => null),
          this.store
            .query('milestones/expectation', { goalSubjectId: subjectId })
            .catch(() => null),
          ...(progressVisualSubjectIds.has(subjectId)
            ? [
                this.store
                  .query('milestones/progression', {
                    goalSubjectId: subjectId,
                    type: ProgressionType.PROGRESSION,
                    latest: true,
                  })
                  .catch(() => null),
              ]
            : []),
        ]),
      ),
    );

    let students = sortBy(
      reviewReports
        .map((r) =>
          this.store.peekRecord('person', r.belongsTo('studentId').id()),
        )
        .filter(Boolean),
      'fullName',
    );

    let reportMap = {};
    reviewReports.forEach((r) => {
      reportMap[r.belongsTo('studentId').id()] = r;
    });

    return { template, students, reportMap, periodIndex, year };
  }

  setupController(controller, model) {
    super.setupController(...arguments);
    let groupCtrl = this.controllerFor('reporting.group');
    groupCtrl.set('isReviewRoute', true);

    let template = model.template;
    // Always rebuild: displayName is set on the shared template record by
    // whichever screen touched it last, so a cached value can name a different
    // period than the one being reviewed.
    // Named for the period actually being reviewed, not whichever is current —
    // they differ whenever a historic card routed here. The counts entry is
    // only authoritative when it describes this same period, which
    // templateDisplayName checks.
    let entry = (groupCtrl.templateDraftInfo || {})[template.get('id')];
    let label = periodLabelAt(template, model.periodIndex, entry);
    let displayName = templateDisplayName(
      template,
      model.periodIndex,
      model.year,
      entry,
    );
    template.set('displayName', displayName);
    groupCtrl.set('activeReport', template);

    // Header line for the group review screen. Kept off the template record
    // for the same reason as the view route: displayName is shared with
    // screens that aren't group-scoped.
    let group = this.modelFor('reporting.group');
    controller.set(
      'reviewHeading',
      [
        group && group.get('bannerText'),
        template.get('name'),
        model.year,
        label,
      ]
        .filter(Boolean)
        .join(' - '),
    );

    controller.set('reviewReportsOpen', true);
    controller.set('reviewStudents', model.students);
    controller.set('reviewStudentReports', model.reportMap);

    if (model.students.length) {
      controller.selectReviewStudent(model.students[0]);
    }
  }

  resetController(controller, isExiting) {
    if (isExiting) {
      let groupCtrl = this.controllerFor('reporting.group');
      groupCtrl.set('isReviewRoute', false);
      controller.cleanupReviewView();
      // Period params are sticky per template. Cleared so the next visit that
      // doesn't name a period gets the current one, not whichever period was
      // last opened here (often a historic card's).
      controller.periodIndex = null;
      controller.periodEndDate = null;
      controller.periodYear = null;
    }
  }
}
